import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { HubFramework } from '../../HubFramework'
import { HubPathRouting } from '../../HubFramework/pathBuilder'
import { getProjectMeta, getProjectMetaDataAreas, getProjectUsersData } from './BackendFacade/apiCalls'
import { moduleContent } from './moduleContent'
import { Area, DoopUser, ProjectMeta } from './Interfaces/IDOOP'
import { DoopDataContext } from './doopDataContext'
import { useMain } from '../../Utils/SessionControls/mainContext'

type DoopState = {
  meta: ProjectMeta | null
  users: DoopUser[]
  areas: Area[]
  error: string
}

const initialState: DoopState = {
  meta: null,
  users: [],
  areas: [],
  error: ''
}

export interface OptionOIS {
  value: string
  label: string
}

export const Module: React.FC = () => {
  const [state, setState] = useState<DoopState>(initialState)
  const [loading, setLoading] = useState(false)
  const { setLoading: setGlobalLoading } = useMain()

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const [metaResp, areaResp, usersResp] = await Promise.all([
        getProjectMeta(HubPathRouting.currentProject.id, HubPathRouting.currentModule.id),
        getProjectMetaDataAreas(HubPathRouting.currentProject.id, HubPathRouting.currentModule.id),
        getProjectUsersData(HubPathRouting.currentProject.id, HubPathRouting.currentModule.id)
      ])

      const meta = metaResp.data as ProjectMeta
      if (meta && meta.doopAreas === undefined) {
        meta.doopAreas = []
      }

      setState({
        meta: meta ?? null,
        users: (usersResp.data as DoopUser[]) ?? [],
        areas: (areaResp.data.allAreas as Area[]) ?? [],
        error: ''
      })
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : String(error)
      }))
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    HubPathRouting.setProject()
    HubPathRouting.setModule()
  }, [])

  useEffect(() => {
    setGlobalLoading(loading)
  }, [loading, setGlobalLoading])

  useEffect(() => {
    refresh().catch(() => undefined)
  }, [refresh])

  const contextValue = useMemo(
    () => ({
      meta: state.meta,
      users: state.users,
      areas: state.areas,
      loading,
      error: state.error,
      refresh
    }),
    [state.meta, state.users, state.areas, state.error, loading, refresh]
  )

  return (
    <DoopDataContext.Provider value={contextValue}>
      <HubFramework hubContent={moduleContent} module={contextValue} />
    </DoopDataContext.Provider>
  )
}
